import type { AnalyticsDays, PageResult, PlatformAnalyticsOverview, TenantAnalytics, TenantAnalyticsListItem } from './analytics-types'
import type { BackupObservabilitySummary, BackupPageResult, BackupRun, RestoreDrill } from './backup-types'
import type { ReleaseOverview, Release, DeploymentPlan, DeploymentInput, DeploymentDetail, DeploymentJob } from './deployment-types'

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1'
let accessToken: string | null = null
let refreshPromise: Promise<boolean> | null = null

export class ApiError extends Error {
  constructor(public code: string, message: string, public status: number) { super(message) }
}

export const setPlatformToken = (token: string | null) => { accessToken = token }

async function refresh() {
  if (refreshPromise) return refreshPromise
  refreshPromise = fetch(`${API_BASE}/platform/auth/refresh`, { method: 'POST', credentials: 'include' })
    .then(async (response) => {
      if (!response.ok) return false
      const body = await response.json()
      accessToken = body.data.accessToken
      return true
    }).catch(() => false).finally(() => { refreshPromise = null })
  return refreshPromise
}

async function request<T>(path: string, init: RequestInit = {}, retry = true, token = accessToken): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !(init.body instanceof FormData)) headers.set('content-type', 'application/json')
  if (token) headers.set('authorization', `Bearer ${token}`)
  const response = await fetch(`${API_BASE}${path}`, { ...init, headers, credentials: 'include' })
  if (response.status === 401 && retry && token === accessToken && await refresh()) return request(path, init, false)
  if (!response.ok) {
    const body = await response.json().catch(() => null)
    throw new ApiError(body?.error?.code ?? 'REQUEST_FAILED', body?.error?.message ?? 'Zahtjev nije uspio.', response.status)
  }
  if (response.status === 204) return undefined as T
  return response.json()
}

const body = (method: string, value?: unknown): RequestInit => ({ method, body: value === undefined ? undefined : JSON.stringify(value) })
const data = async <T>(path: string, init?: RequestInit) => (await request<{ data: T }>(path, init)).data
export type QueryInput = Record<string, string | number | boolean | null | undefined>
export interface ReviewCredentials {
  displayName: string
  email: string
  password: string
  updated_at: string
}
export interface ReviewMemberUpdateInput {
  displayName?: string
  email?: string
}
export interface PlatformTelegramAiStatus {
  feature_enabled: boolean
  configured: boolean
  service_status: 'ready' | 'disabled' | 'checking' | 'temporarily_unavailable' | 'misconfigured'
  service_error_code: string | null
  bot_username: string | null
  connected: boolean
  connected_at: string | null
  managed_by_superadmin: boolean
  managed_owner_name: string
}
export interface PlatformTelegramAiOwner {
  owner_id: string
  owner_name: string
  connected: boolean
  connected_at: string | null
}
export interface PlatformTelegramAiOwners {
  feature_enabled: boolean
  configured: boolean
  service_status: PlatformTelegramAiStatus['service_status']
  service_error_code: string | null
  bot_username: string | null
  owners: PlatformTelegramAiOwner[]
}
export interface PlatformTelegramLinkCode { code: string; expires_at: string; deep_link: string }
export const queryString = (input: QueryInput = {}) => {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(input)) if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
  const encoded = params.toString()
  return encoded ? `?${encoded}` : ''
}

export const platformApi = {
  deploymentReleases: () => data<ReleaseOverview>('/platform/deployments/releases'),
  deploymentCheck: (sha?: string) => data<Release>('/platform/deployments/check', body('POST', sha ? { sha } : {})),
  deploymentPreview: (sha: string, action: 'install' | 'rollback') => data<DeploymentPlan>('/platform/deployments/preview', body('POST', { sha, action })),
  deploymentStart: (input: DeploymentInput) => data<{ job: DeploymentJob; status_token: string }>('/platform/deployments/jobs', body('POST', input)),
  deploymentJobs: () => data<DeploymentJob[]>('/platform/deployments/jobs'),
  deploymentJob: (id: string) => data<DeploymentDetail>(`/platform/deployments/jobs/${id}`),
  login: (email: string, password: string) => data<any>('/platform/auth/login', body('POST', { email, password })),
  acceptInvitation: async (token: string, fullName: string, password: string) =>
    (await request<{ data: any }>('/platform/auth/invitations/accept', body('POST', { token, full_name: fullName, password }), false, null)).data,
  verifyMfa: async (challengeToken: string, code?: string, recoveryCode?: string) => {
    const result = await data<any>('/platform/auth/mfa/verify', body('POST', { challenge_token: challengeToken, code: code || undefined, recovery_code: recoveryCode || undefined }))
    setPlatformToken(result.accessToken)
    return result
  },
  restore: async () => { if (!await refresh()) return null; return data<any>('/platform/auth/me') },
  me: () => data<any>('/platform/auth/me'),
  logout: async () => { try { await request('/platform/auth/logout', body('POST')) } finally { setPlatformToken(null) } },
  health: () => request<any>('/health/ready', {}, false, null),
  dashboard: () => data<any>('/platform/dashboard'),
  backupSummary: () => data<BackupObservabilitySummary>('/platform/backup-observability/summary'),
  backupRuns: async (query: QueryInput = {}): Promise<BackupPageResult<BackupRun>> => {
    const result = await request<{ data: BackupRun[]; meta: BackupPageResult<BackupRun>['meta'] }>(`/platform/backup-observability/runs${queryString({ page: 1, page_size: 25, ...query })}`)
    return { data: result.data, meta: result.meta }
  },
  restoreDrills: async (query: QueryInput = {}): Promise<BackupPageResult<RestoreDrill>> => {
    const result = await request<{ data: RestoreDrill[]; meta: BackupPageResult<RestoreDrill>['meta'] }>(`/platform/backup-observability/restore-drills${queryString({ page: 1, page_size: 25, ...query })}`)
    return { data: result.data, meta: result.meta }
  },
  analytics: (days: AnalyticsDays = 30) => data<PlatformAnalyticsOverview>(`/platform/analytics${queryString({ days })}`),
  analyticsTenants: (query: QueryInput = {}) => data<PageResult<TenantAnalyticsListItem>>(`/platform/analytics/tenants${queryString({ page: 1, page_size: 25, days: 30, ...query })}`),
  tenantAnalytics: (id: string, days: AnalyticsDays = 30) => data<TenantAnalytics>(`/platform/tenants/${id}/analytics${queryString({ days })}`),
  tenants: (query: QueryInput = {}) => data<any>(`/platform/tenants${queryString({ page: 1, page_size: 25, ...query })}`),
  tenant: (id: string) => data<any>(`/platform/tenants/${id}`),
  reviewCredentials: (id: string) => data<ReviewCredentials>(`/platform/tenants/${id}/review-credentials`),
  regenerateReviewCredentials: (id: string) => data<ReviewCredentials>(`/platform/tenants/${id}/review-credentials/regenerate`, body('POST')),
  updateReviewMember: (id: string, input: ReviewMemberUpdateInput) => data<ReviewCredentials>(`/platform/tenants/${id}/review-member`, body('PATCH', input)),
  resetReviewMemberEmail: (id: string) => data<ReviewCredentials>(`/platform/tenants/${id}/review-member/reset-email`, body('POST')),
  createTenant: (input: unknown) => data<any>('/platform/tenants', body('POST', input)),
  updateTenantProfile: (id: string, input: unknown) => data<any>(`/platform/tenants/${id}/profile`, body('PATCH', input)),
  setTenantSharedApp: (id: string, showInSharedApp: boolean) => data<{ id: string; show_in_shared_app: boolean }>(`/platform/tenants/${id}/shared-app`, body('PATCH', { show_in_shared_app: showInSharedApp })),
  sendOwnerInvitation: (id: string) => data<any>(`/platform/tenants/${id}/owner-invitation/send`, body('POST')),
  resendOwnerInvitation: (id: string) => data<any>(`/platform/tenants/${id}/owner-invitation/resend`, body('POST')),
  setTenantStatus: (id: string, status: string, reason: string) => data<any>(`/platform/tenants/${id}/status`, body('PATCH', { status, reason })),
  setSubscription: (id: string, input: unknown) => data<any>(`/platform/tenants/${id}/subscription`, body('PATCH', input)),
  tenantFeatures: (id: string) => data<any[]>(`/platform/tenants/${id}/features`),
  setTenantFeature: (id: string, key: string, enabled: boolean | null, reason: string) => data<any[]>(`/platform/tenants/${id}/features/${key}`, body('PATCH', { enabled, reason })),
  tenantTelegramAi: (id: string) => data<PlatformTelegramAiStatus>(`/platform/tenants/${id}/telegram-ai`),
  createTenantTelegramLink: (id: string) => data<PlatformTelegramLinkCode>(`/platform/tenants/${id}/telegram-ai/link-code`, body('POST')),
  unlinkTenantTelegramAi: (id: string) => data<{ connected: false }>(`/platform/tenants/${id}/telegram-ai/binding`, body('DELETE')),
  tenantTelegramAiOwners: (id: string) => data<PlatformTelegramAiOwners>(`/platform/tenants/${id}/telegram-ai/owners`),
  createTenantOwnerTelegramLink: (id: string, ownerId: string) => data<PlatformTelegramLinkCode>(`/platform/tenants/${id}/telegram-ai/owners/${ownerId}/link-code`, body('POST')),
  unlinkTenantOwnerTelegramAi: (id: string, ownerId: string) => data<{ connected: false }>(`/platform/tenants/${id}/telegram-ai/owners/${ownerId}/binding`, body('DELETE')),
  tenantMetrics: (id: string) => data<any[]>(`/platform/tenants/${id}/metrics`),
  refreshMetrics: (id: string) => data<any>(`/platform/tenants/${id}/metrics/refresh`, body('POST')),
  onboarding: (id: string, key: string, done: boolean, note?: string) => data<any>(`/platform/tenants/${id}/onboarding/${key}`, body('PATCH', { done, note })),
  plans: (query: QueryInput = {}) => data<any>(`/platform/plans${queryString({ page: 1, page_size: 25, ...query })}`),
  createPlan: (input: unknown) => data<any>('/platform/plans', body('POST', input)),
  updatePlan: (id: string, input: unknown) => data<any>(`/platform/plans/${id}`, body('PATCH', input)),
  deletePlan: (id: string) => request<void>(`/platform/plans/${id}`, body('DELETE')),
  features: (query: QueryInput = {}) => data<any>(`/platform/features${queryString({ page: 1, page_size: 25, ...query })}`),
  createFeature: (input: unknown) => data<any>('/platform/features', body('POST', input)),
  updateFeature: (key: string, input: unknown) => data<any>(`/platform/features/${key}`, body('PATCH', input)),
  deleteFeature: (key: string) => request<void>(`/platform/features/${key}`, body('DELETE')),
  admins: (query: QueryInput = {}) => data<any>(`/platform/admins${queryString({ page: 1, page_size: 25, ...query })}`),
  inviteAdmin: (input: unknown) => data<any>('/platform/admins/invitations', body('POST', input)),
  adminInvitations: (query: QueryInput = {}) => data<any>(`/platform/admins/invitations${queryString({ page: 1, page_size: 25, state: 'pending', ...query })}`),
  resendAdminInvitation: (id: string) => data<any>(`/platform/admins/invitations/${id}/resend`, body('POST')),
  revokeAdminInvitation: (id: string) => data<any>(`/platform/admins/invitations/${id}`, body('DELETE')),
  updateAdmin: (id: string, active: boolean) => data<any>(`/platform/admins/${id}`, body('PATCH', { active })),
  audit: (query: QueryInput = {}) => data<any>(`/platform/audit${queryString({ page: 1, page_size: 25, ...query })}`),
  impersonations: (query: QueryInput = {}) => data<any>(`/platform/impersonations${queryString({ page: 1, page_size: 25, state: 'all', ...query })}`),
  startImpersonation: (tenantId: string, input: unknown) => data<any>(`/platform/tenants/${tenantId}/impersonation`, body('POST', input)),
  endImpersonation: (id: string) => data<any>(`/platform/impersonations/${id}/end`, body('POST')),
  settings: () => data<any[]>('/platform/settings'),
  updateSetting: (key: string, value: boolean) => data<any>(`/platform/settings/${key}`, body('PATCH', { value })),
  trialAcquisitionSettings: () => data<any>('/platform/trial-acquisition/settings'),
  updateTrialAcquisitionSettings: (input: unknown) => data<any>('/platform/trial-acquisition/settings', body('PATCH', input)),
  leads: (query: QueryInput = {}) => data<any>(`/platform/leads${queryString({ page: 1, page_size: 25, ...query })}`),
  lead: (id: string) => data<any>(`/platform/leads/${id}`),
  updateLeadSales: (id: string, input: unknown) => data<any>(`/platform/leads/${id}/sales`, body('PATCH', input)),
  resendLeadVerification: (id: string) => data<any>(`/platform/leads/${id}/resend-verification`, body('POST')),
  allowNewLeadTrial: (id: string) => data<any>(`/platform/leads/${id}/allow-new-trial`, body('POST')),
  onboardingDefinitions: (query: QueryInput = {}) => data<any>(`/platform/onboarding-definitions${queryString({ page: 1, page_size: 25, ...query })}`),
  createOnboardingDefinition: (input: unknown) => data<any>('/platform/onboarding-definitions', body('POST', input)),
  updateOnboardingDefinition: (key: string, input: unknown) => data<any>(`/platform/onboarding-definitions/${key}`, body('PATCH', input)),
}
